import { useState, useEffect } from "react"
import { useAuth } from "./hooks/useAuth"
import { LoginPage } from "./components/LoginPage"
import { Navbar } from "./components/Navbar"
import { AdminDashboard } from "./components/AdminDashboard"
import { UserTimeEntry2 } from "./components/UserTimeEntry2"
import { UserDashboard } from "./components/UserDashboard"
import { ProjectManagement } from "./components/ProjectManagement"
import { UserManagement } from "./components/UserManagement"
import { TeamSelectionModal } from "./components/TeamSelectionModal"
import { WeeklyReport } from "./components/WeeklyReport"
import { WeeklyReportReview } from "./components/WeeklyReportReview"
import { TeamDashboard } from "./components/TeamDashboard"
import { TimeEntrySubmissionStatus } from "./components/TimeEntrySubmissionStatus"
import { SubmittedReportsView } from "./components/SubmittedReportsView"
import { QuotationManagement } from "./components/QuotationManagement"
import { EquipmentHistory } from "./components/EquipmentHistory"
import { SpecManagement } from "./components/SpecManagement"
import { ScheduleManagement } from "./components/ScheduleManagement"
import { TeamCalendar } from "./components/TeamCalendar"
import { MyTodoList } from "./components/MyTodoList"
import { AnnouncementManagement } from "./components/AnnouncementManagement"
import { AnnouncementPopup } from "./components/AnnouncementPopup"
import { lumi } from "./lib/lumi"
import { Toaster } from "react-hot-toast"
import toast from "react-hot-toast"
import {
  parseIssueTargetFromUrl,
  clearIssueTargetFromUrl,
  setPendingIssueTarget,
} from "./lib/issueLink"

// ==================== 페이지 타입 및 캐시 유틸 ====================
type PageKey =
  | "dashboard"
  | "timeEntry"
  | "projects"
  | "users"
  | "weeklyReport"
  | "weeklyReportReview"
  | "submissionStatus"
  | "submittedReports"
  | "quotations"
  | "equipmentHistory"
  | "specManagement"
  | "schedule"
  | "teamCalendar"
  | "myTodos"
  | "announcementManagement"

// 마지막으로 보던 페이지를 브라우저에 저장하기 위한 localStorage 키
const LAST_PAGE_STORAGE_KEY = "pavetech:lastPage"

// 역할별로 접근 가능한 페이지 목록 (라우팅 분기와 일치하도록 유지)
const ALLOWED_PAGES: Record<string, PageKey[]> = {
  // Admin / C-Level은 개인 To do 목록(myTodos) 미사용
  ADMIN: [
    "dashboard", "projects", "users", "weeklyReportReview",
    "submissionStatus", "submittedReports", "quotations",
    "equipmentHistory", "specManagement", "schedule", "teamCalendar",
    "announcementManagement",
  ],
  C_LEVEL: [
    "dashboard", "timeEntry", "weeklyReport", "weeklyReportReview",
    "submittedReports", "quotations", "equipmentHistory", "specManagement",
    "schedule", "teamCalendar",
  ],
  MANAGER: [
    "dashboard", "timeEntry", "weeklyReport", "weeklyReportReview",
    "submittedReports", "quotations", "equipmentHistory", "specManagement",
    "schedule", "teamCalendar", "myTodos",
  ],
  USER: [
    "dashboard", "timeEntry", "weeklyReport",
    "submittedReports", "equipmentHistory", "specManagement", "schedule",
    "teamCalendar", "myTodos",
  ],
}

// 역할별 기본 홈 페이지 (접근 불가/캐시 없음 시 폴백)
function getDefaultPage(roleKey: string): PageKey {
  if (roleKey === "ADMIN") return "dashboard"
  if (roleKey === "C_LEVEL") return "dashboard"
  return "timeEntry"
}

// 현재 사용자 역할 키 계산
function resolveRoleKey(auth: ReturnType<typeof useAuth>): string {
  if (auth.isAdmin) return "ADMIN"
  if (auth.isCLevel) return "C_LEVEL"
  if (auth.isManager) return "MANAGER"
  return "USER"
}

// 저장된 페이지가 현재 역할에서 접근 가능하면 그대로, 아니면 기본 홈으로 폴백
function resolveInitialPage(roleKey: string): PageKey {
  const defaultPage = getDefaultPage(roleKey)
  try {
    const saved = localStorage.getItem(LAST_PAGE_STORAGE_KEY) as PageKey | null
    const allowed = ALLOWED_PAGES[roleKey] || ALLOWED_PAGES.USER
    if (saved && allowed.includes(saved)) {
      return saved
    }
  } catch {
    // localStorage 접근 불가 환경에서는 조용히 기본값 사용
  }
  return defaultPage
}

// ==================== 새로운 App 구현 ====================
function App() {
  const auth = useAuth()
  const [currentPage, setCurrentPage] = useState<PageKey>("timeEntry")
  const [showTeamModal, setShowTeamModal] = useState(false)
  const [selectedWeekStart, setSelectedWeekStart] = useState<string | null>(null)
  const [selectedWeekEnd, setSelectedWeekEnd] = useState<string | null>(null)
  // 초기 페이지 복원을 역할 확정 후 1회만 수행하기 위한 플래그
  const [restoredInitialPage, setRestoredInitialPage] = useState(false)
  // 이메일 딥링크로 진입한 세션 여부.
  // true면 공지 팝업을 건너뛰고 dismiss 키도 저장하지 않아, 다음 일반 접속 시 정상 표시된다.
  // URL 쿼리가 정리된 뒤에도 같은 세션에서 뒤늦게 뜨지 않도록 이 플래그는 계속 유지한다.
  const [skipAnnouncement, setSkipAnnouncement] = useState(false)

  // 역할 확정 후: (1) 이메일 딥링크가 있으면 우선 처리, (2) 없으면 마지막 페이지 복원
  // 최초 1회만 수행 (이후 사용자의 수동 페이지 이동을 덮어쓰지 않음)
  useEffect(() => {
    if (!auth.user) return
    // Admin은 즉시, 일반 사용자는 프로필 초기화 완료 후에 복원
    if (!auth.isAdmin && !auth.isInitialized) return
    if (restoredInitialPage) return

    const roleKey = resolveRoleKey(auth)
    const allowed = ALLOWED_PAGES[roleKey] || ALLOWED_PAGES.USER

    // (1) 이메일 딥링크(?page=equipmentHistory&equipmentId=...&issueId=...) 우선 처리
    const target = parseIssueTargetFromUrl()
    if (target) {
      // 딥링크는 항상 소비 후 URL을 정리해 새로고침 시 중복 진입 방지
      clearIssueTargetFromUrl()
      // 딥링크 진입 세션 동안에는 공지 팝업을 표시하지 않음
      setSkipAnnouncement(true)
      if (allowed.includes("equipmentHistory")) {
        // 장비이력 화면이 pendingIssueTarget을 소비해 해당 이슈로 스크롤/하이라이트
        setPendingIssueTarget(target)
        setCurrentPage("equipmentHistory")
        setRestoredInitialPage(true)
        return
      }
      // 권한이 없거나 접근 불가한 경우: 안내 후 기본 홈으로 폴백
      toast.error("해당 이슈에 접근할 권한이 없습니다.")
      setCurrentPage(getDefaultPage(roleKey))
      setRestoredInitialPage(true)
      return
    }

    // (2) 딥링크가 없으면 마지막으로 보던 페이지 복원(권한 폴백 포함)
    setCurrentPage(resolveInitialPage(roleKey))
    setRestoredInitialPage(true)
  }, [auth.user, auth.isAdmin, auth.isInitialized, auth.userRole, auth.isCLevel, auth.isManager, restoredInitialPage])

  // currentPage 변경 시 localStorage에 저장 (다음 새로고침/재접속 시 복원)
  useEffect(() => {
    if (!restoredInitialPage) return
    try {
      localStorage.setItem(LAST_PAGE_STORAGE_KEY, currentPage)
    } catch {
      // 저장 실패는 무시 (기능 비필수)
    }
  }, [currentPage, restoredInitialPage])

  // 주간보고 → 장비 이슈 딥링크: 연결 이슈 배지 클릭 시 장비이력 화면으로 전환
  useEffect(() => {
    const handleNavigateToEquipmentIssue = () => {
      setCurrentPage("equipmentHistory")
    }
    window.addEventListener("navigateToEquipmentIssue", handleNavigateToEquipmentIssue)
    return () => window.removeEventListener("navigateToEquipmentIssue", handleNavigateToEquipmentIssue)
  }, [])

  // 로그인되지 않은 경우
  if (!auth.user) {
    return <LoginPage />
  }

  // 초기화 중 또는 프로필 로딩 중
  if (!auth.isAdmin && (!auth.isInitialized || auth.isLoadingProfile)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-200 border-t-orange-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">로딩 중...</p>
        </div>
      </div>
    )
  }

  // Admin은 승인 체크 없이 바로 진입
  if (auth.isAdmin) {
    const isWeeklyReportReviewPage = currentPage === "weeklyReportReview"
    return (
      <div className={`min-h-screen ${isWeeklyReportReviewPage ? "bg-gradient-to-br from-orange-50 to-amber-50" : "bg-gray-50"}`}>
        <Toaster position="top-right" />
        <Navbar 
          currentPage={currentPage} 
          setCurrentPage={setCurrentPage}
          onNavigateToWeek={(weekStart, weekEnd) => {
            setSelectedWeekStart(weekStart)
            setSelectedWeekEnd(weekEnd)
          }}
          isManager={false}
          isCLevel={false}
          userRole="ADMIN"
          teamName=""
        />
        <main className="max-w-7xl mx-auto">
          {currentPage === "projects" ? (
            <ProjectManagement />
          ) : currentPage === "users" ? (
            <UserManagement />
          ) : currentPage === "weeklyReportReview" ? (
            <WeeklyReportReview />
          ) : currentPage === "submissionStatus" ? (
            <TimeEntrySubmissionStatus />
          ) : currentPage === "submittedReports" ? (
            <SubmittedReportsView />
          ) : currentPage === "quotations" ? (
            <QuotationManagement />
          ) : currentPage === "equipmentHistory" ? (
            <EquipmentHistory />
          ) : currentPage === "specManagement" ? (
            <SpecManagement />
          ) : currentPage === "schedule" ? (
            <ScheduleManagement />
          ) : currentPage === "teamCalendar" ? (
            <TeamCalendar />
          ) : currentPage === "announcementManagement" ? (
            <AnnouncementManagement />
          ) : (
            <AdminDashboard />
          )}
        </main>
        {/* 공지 팝업: Admin도 진입 페이지와 무관하게 세션당 1회 판정 */}
        <AnnouncementPopup ready={restoredInitialPage} skip={skipAnnouncement} />
      </div>
    )
  }

  // 승인 거절된 경우
  if (auth.approvalStatus === "rejected") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-rose-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
          <div className="text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full mx-auto mb-6 flex items-center justify-center">
              <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">로그인 불가</h2>
            <p className="text-gray-600 mb-6">
              관리자에 의해 승인이 거부되었습니다.<br />
              자세한 사항은 관리자에게 문의해주세요.
            </p>
            <button
              onClick={() => lumi.auth.signOut()}
              className="w-full bg-gradient-to-r from-orange-600 to-amber-600 text-white py-3 px-4 rounded-lg font-semibold hover:from-orange-700 hover:to-amber-700 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              돌아가기
            </button>
          </div>
        </div>
      </div>
    )
  }

  // 승인 대기 중인 경우
  if (auth.approvalStatus === "pending") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
          <div className="text-center">
            <div className="w-20 h-20 bg-amber-100 rounded-full mx-auto mb-6 flex items-center justify-center">
              <svg className="w-10 h-10 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">승인 대기 중</h2>
            <p className="text-gray-600 mb-6">
              회원가입이 완료되었습니다.<br />
              관리자의 승인을 기다리고 있습니다.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-amber-800">
                ⏳ 승인이 완료되면 시스템을 사용할 수 있습니다.<br />
                승인 후 다시 로그인해주세요.
              </p>
            </div>
            <button
              onClick={() => lumi.auth.signOut()}
              className="w-full bg-gradient-to-r from-orange-600 to-amber-600 text-white py-3 px-4 rounded-lg font-semibold hover:from-orange-700 hover:to-amber-700 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              돌아가기
            </button>
          </div>
        </div>
      </div>
    )
  }

  // 승인 완료 (approved) - 팀 선택 필요 여부 확인
  if (!auth.hasSelectedTeam) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TeamSelectionModal
          userId={auth.user.userId}
          userName={auth.user.userName}
          email={auth.user.email}
          onComplete={() => {
            // 팀 선택 완료 후 프로필 새로고침
            auth.refreshProfile()
          }}
        />
      </div>
    )
  }

  // 메인 화면 (승인 완료 + 팀 선택 완료)
  const isWeeklyReportReviewPage = currentPage === "weeklyReportReview"
  const isWeeklyReportPage = currentPage === "weeklyReport"
  const isOrangeBgPage = isWeeklyReportReviewPage || isWeeklyReportPage
  return (
    <>
      <Toaster position="top-right" />
      <div className={`min-h-screen ${isOrangeBgPage ? "bg-gradient-to-br from-orange-50 to-amber-50" : "bg-gray-50"}`}>
      <Navbar 
        currentPage={currentPage} 
        setCurrentPage={setCurrentPage}
        onNavigateToWeek={(weekStart, weekEnd) => {
          setSelectedWeekStart(weekStart)
          setSelectedWeekEnd(weekEnd)
        }}
        isManager={auth.isManager}
        isCLevel={auth.isCLevel}
        userRole={auth.userRole}
        teamName={auth.teamName}
        userName={auth.userName}
      />
      <main className="max-w-7xl mx-auto">
        {auth.isCLevel ? (
          // C-level: 관리자 대시보드 + 투입시간 입력 + 주간보고 작성 + 주간보고 확인 (전체)
          currentPage === "weeklyReportReview" ? (
            <WeeklyReportReview />
          ) : currentPage === "weeklyReport" ? (
            <WeeklyReport />
          ) : currentPage === "timeEntry" ? (
            <UserTimeEntry2 />
          ) : currentPage === "submittedReports" ? (
            <SubmittedReportsView />
          ) : currentPage === "quotations" ? (
            <QuotationManagement />
          ) : currentPage === "equipmentHistory" ? (
            <EquipmentHistory />
          ) : currentPage === "specManagement" ? (
            <SpecManagement />
          ) : currentPage === "schedule" ? (
            <ScheduleManagement />
          ) : currentPage === "teamCalendar" ? (
            <TeamCalendar />
          ) : (
            <AdminDashboard />
          )
        ) : auth.isManager ? (
          // Manager: 투입시간 입력 + 팀 대시보드(teamId 필터링) + 주간보고 + 주간보고 확인(teamId 필터링)
          currentPage === "dashboard" ? (
            <TeamDashboard />
          ) : currentPage === "weeklyReport" ? (
            <WeeklyReport />
          ) : currentPage === "weeklyReportReview" ? (
            <WeeklyReportReview />
          ) : currentPage === "submittedReports" ? (
            <SubmittedReportsView />
          ) : currentPage === "quotations" ? (
            <QuotationManagement />
          ) : currentPage === "equipmentHistory" ? (
            <EquipmentHistory />
          ) : currentPage === "specManagement" ? (
            <SpecManagement />
          ) : currentPage === "schedule" ? (
            <ScheduleManagement />
          ) : currentPage === "teamCalendar" ? (
            <TeamCalendar />
          ) : currentPage === "myTodos" ? (
            <MyTodoList />
          ) : (
            <UserTimeEntry2 />
          )
        ) : (
          // User: 투입시간 입력 + 대시보드 + 주간보고
          currentPage === "dashboard" ? (
            <UserDashboard setCurrentPage={setCurrentPage} />
          ) : currentPage === "weeklyReport" ? (
            <WeeklyReport />
          ) : currentPage === "submittedReports" ? (
            <SubmittedReportsView />
          ) : currentPage === "equipmentHistory" ? (
            <EquipmentHistory />
          ) : currentPage === "specManagement" ? (
            <SpecManagement />
          ) : currentPage === "schedule" ? (
            <ScheduleManagement />
          ) : currentPage === "teamCalendar" ? (
            <TeamCalendar />
          ) : currentPage === "myTodos" ? (
            <MyTodoList />
          ) : (
            <UserTimeEntry2 />
          )
        )}
      </main>
      {/* 공지 팝업: 일반 사용자(임원/팀장/사용자) 공통 마운트 — 진입 페이지와 무관하게 표시 */}
      <AnnouncementPopup ready={restoredInitialPage} skip={skipAnnouncement} />
    </div>
    </>
  )
}

export default App

/* ==================== 기존 코드 (백업용) ====================
import { useState, useEffect } from "react"
import { useAuth } from "./hooks/useAuth"
import { LoginPage } from "./components/LoginPage"
import { Navbar } from "./components/Navbar"
import { AdminDashboard } from "./components/AdminDashboard"
import { UserTimeEntry } from "./components/UserTimeEntry"
import { UserDashboard } from "./components/UserDashboard"
import { ProjectManagement } from "./components/ProjectManagement"
import { UserManagement } from "./components/UserManagement"
import { TeamSelectionModal } from "./components/TeamSelectionModal"
import { WeeklyReport } from "./components/WeeklyReport"
import { WeeklyReportReview } from "./components/WeeklyReportReview"
import { TeamDashboard } from "./components/TeamDashboard"
import { lumi } from "./lib/lumi"

function App() {
  const { user, isAdmin, userRole, isCLevel, isManager, refreshUserProfile, setTeamId, setTeamName, setUserName, setUserRole } = useAuth()
  const [currentPage, setCurrentPage] = useState<"dashboard" | "timeEntry" | "projects" | "users" | "weeklyReport" | "weeklyReportReview">("timeEntry")
  const [showTeamModal, setShowTeamModal] = useState(false)
  const [userProfile, setUserProfile] = useState<any>(null)
  const [isPendingApproval, setIsPendingApproval] = useState(false)
  const [isLoadingProfile, setIsLoadingProfile] = useState(false)
  const [selectedWeekStart, setSelectedWeekStart] = useState<string | null>(null)
  const [selectedWeekEnd, setSelectedWeekEnd] = useState<string | null>(null)

  // userRole이 로드되면 역할에 맞는 초기 페이지로 변경
  useEffect(() => {
    if (userRole && !isPendingApproval) {
      console.log("🔄 역할 기반 페이지 설정:", {
        userRole,
        isAdmin,
        isCLevel,
        isManager
      })
      
      if (isAdmin || isCLevel) {
        setCurrentPage("dashboard")
      } else if (isManager) {
        // Manager는 timeEntry를 기본으로 (팀 대시보드는 메뉴에서 선택)
        setCurrentPage("timeEntry")
      }
    }
  }, [userRole, isAdmin, isCLevel, isManager, isPendingApproval])

  useEffect(() => {
    if (user) {
      if (isAdmin) {
        // (1) Admin은 프로필 체크 없이 바로 진입
        console.log("👑 Admin 사용자 - 바로 진입")
        setIsPendingApproval(false)
        setIsLoadingProfile(false)
        setShowTeamModal(false)
      } else {
        // 일반 사용자는 프로필 체크 시작
        console.log("👤 일반 사용자 - 프로필 체크 시작")
        setIsLoadingProfile(true)
        checkUserProfile()
      }
    } else {
      // 로그아웃 상태
      setIsLoadingProfile(false)
      setIsPendingApproval(false)
      setShowTeamModal(false)
    }
  }, [user, isAdmin])

  const checkUserProfile = async (retryCount = 0) => {
    if (!user) {
      setIsLoadingProfile(false)
      return
    }
    
    try {
      console.log(`🔍 프로필 조회 시도 (${retryCount + 1}/3):`, user.userId)
      const result = await lumi.entities.userProfiles3.list({
        filter: { userId: user.userId },
        limit: 1
      })
      
      if (result.list.length === 0) {
        // 프로필이 없는 경우 - 최대 3번 재시도
        if (retryCount < 2) {
          console.log(`⏳ 프로필을 찾을 수 없습니다. ${retryCount + 1}초 후 재시도...`)
          await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)))
          return checkUserProfile(retryCount + 1)
        }
        
        // (2) 3번 재시도 후에도 프로필이 없으면 승인 대기
        console.log("❌ 프로필 조회 실패 (3회 시도) - 승인 대기 상태")
        setIsPendingApproval(true)
        setIsLoadingProfile(false)
        setShowTeamModal(false)
        return
      }
      
      const profile = result.list[0]
      console.log("📋 프로필 발견:", {
        userId: profile.userId,
        approvalStatus: profile.approvalStatus,
        hasSelectedTeam: profile.hasSelectedTeam,
        role: profile.role
      })
      
      // (2) 승인 대기 중인 경우 - 승인 대기 화면 표시
      if (profile.approvalStatus === "pending") {
        console.log("⏳ 승인 대기 중 - 승인 대기 페이지 표시")
        setIsPendingApproval(true)
        setIsLoadingProfile(false)
        setShowTeamModal(false)
        setUserProfile(null)
        return
      }
      
      // (2) 승인 거절된 경우 - 승인 대기 화면 표시
      if (profile.approvalStatus === "rejected") {
        console.log("❌ 승인 거절됨 - 승인 대기 페이지 표시")
        setIsPendingApproval(true)
        setIsLoadingProfile(false)
        setShowTeamModal(false)
        setUserProfile(null)
        return
      }
      
      // (3)(4) 승인이 완료된 경우 - 승인 대기 화면 표시 안 함
      if (profile.approvalStatus === "approved") {
        console.log("✅ 승인 완료 - 홈 화면 진입", {
          role: profile.role,
          teamId: profile.teamId,
          teamName: profile.teamName,
          userName: profile.userName,
        })
        setIsPendingApproval(false)
        setIsLoadingProfile(false)
        setUserProfile(profile)
        
        // 프로필에서 가져온 팀 정보를 useAuth에 직접 설정 (중복 API 호출 방지)
        setTeamId(profile.teamId)
        setTeamName(profile.teamName)
        setUserName(profile.userName)
        setUserRole(profile.role)
        
        console.log("🔄 팀 정보 설정 완료:", {
          teamId: profile.teamId,
          teamName: profile.teamName,
          userName: profile.userName,
          role: profile.role
        })
        
        // (3) 팀을 선택하지 않았으면 팀 선택 모달 표시
        if (!profile.hasSelectedTeam) {
          console.log("🏢 팀 선택 필요 - 모달 표시")
          setShowTeamModal(true)
        } else {
          // (4) 이미 팀 선택 완료 - 홈 화면 바로 표시
          console.log("✅ 팀 선택 완료 - 홈 화면 바로 표시")
          setShowTeamModal(false)
        }
        return
      }
      
      // approvalStatus가 없거나 다른 값인 경우 승인 대기로 처리
      console.log("⚠️ 승인 상태 미확인 - 승인 대기로 처리")
      setIsPendingApproval(true)
      setIsLoadingProfile(false)
      setShowTeamModal(false)
      setUserProfile(null)
    } catch (error) {
      console.error("❌ 프로필 체크 실패:", error)
      // 오류 발생 시 승인 대기로 처리
      setIsPendingApproval(true)
      setIsLoadingProfile(false)
      setShowTeamModal(false)
      setUserProfile(null)
    }
  }

  const handleTeamSelectionComplete = () => {
    setShowTeamModal(false)
    checkUserProfile()
  }

  const handleNavigateToWeek = (weekStart: string, weekEnd: string) => {
    setSelectedWeekStart(weekStart)
    setSelectedWeekEnd(weekEnd)
  }

  if (!user) {
    return <LoginPage />
  }

  // 프로필 로딩 중 (승인된 사용자의 경우 로딩 화면 표시)
  if (!isAdmin && isLoadingProfile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-200 border-t-orange-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">로딩 중...</p>
        </div>
      </div>
    )
  }

  // 승인 대기 중인 사용자
  if (!isAdmin && isPendingApproval) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
          <div className="text-center">
            <div className="w-20 h-20 bg-amber-100 rounded-full mx-auto mb-6 flex items-center justify-center">
              <svg className="w-10 h-10 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">승인 대기 중</h2>
            <p className="text-gray-600 mb-6">
              회원가입이 완료되었습니다.<br />
              관리자의 승인을 기다리고 있습니다.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-amber-800">
                ⏳ 승인이 완료되면 시스템을 사용할 수 있습니다.<br />
                승인 상태는 관리자에게 문의해주세요.
              </p>
            </div>
            <div className="space-y-3">
              <button
                onClick={() => {
                  console.log("🔄 승인 상태 확인 중...")
                  checkUserProfile()
                }}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-4 rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                승인 상태 확인
              </button>
              <button
                onClick={() => lumi.auth.signOut()}
                className="w-full bg-gradient-to-r from-orange-600 to-amber-600 text-white py-3 px-4 rounded-lg font-semibold hover:from-orange-700 hover:to-amber-700 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                로그아웃
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar 
        currentPage={currentPage} 
        setCurrentPage={setCurrentPage}
        onNavigateToWeek={handleNavigateToWeek}
        isManager={isManager}
        isCLevel={isCLevel}
        userRole={userRole}
      />
      <main className="max-w-7xl mx-auto">
        {isAdmin ? (
          // Admin: 모든 페이지 접근 가능
          currentPage === "projects" ? (
            <ProjectManagement />
          ) : currentPage === "users" ? (
            <UserManagement />
          ) : currentPage === "weeklyReportReview" ? (
            <WeeklyReportReview />
          ) : (
            <AdminDashboard />
          )
        ) : isCLevel ? (
          // C-level: 대시보드 및 주간보고 확인 접근 가능
          currentPage === "weeklyReportReview" ? (
            <WeeklyReportReview />
          ) : (
            <AdminDashboard />
          )
        ) : isManager ? (
          // Manager: User 기능 + 팀 대시보드 + 주간보고 확인
          currentPage === "dashboard" ? (
            <TeamDashboard />
          ) : currentPage === "weeklyReport" ? (
            <WeeklyReport />
          ) : currentPage === "weeklyReportReview" ? (
            <WeeklyReportReview />
          ) : (
            <UserTimeEntry />
          )
        ) : (
          // User: 기존 기능 유지
          currentPage === "dashboard" ? <UserDashboard /> : 
          currentPage === "weeklyReport" ? <WeeklyReport /> : 
          <UserTimeEntry />
        )}
      </main>

      {showTeamModal && user && (
        <TeamSelectionModal
          userId={user.userId}
          userName={user.userName}
          email={user.email}
          onComplete={handleTeamSelectionComplete}
        />
      )}
    </div>
  )
}

export default App
*/
